/**
 * Split Unassigned (TMMAPP-0108, the tmm-api review amendment; MM-D-114; `POST
 * /v1/virtual-accounts/{id}/assign/bulk`): the envelopes with an amount each, the served Unassigned
 * balance and what remains of it, then one bulk request — at most 50 lines, all or nothing. Writers
 * only. `409 INSUFFICIENT_TO_ASSIGN` shows the served `details.availableMinor`. The remaining figure
 * is a preview; the page refetches the served balances after the split.
 */
import { useId, useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { allocationsApi } from '@/api/allocations';
import type { Envelope } from '@/api/envelopes';
import { ErrorCodes, isApiError } from '@/api/errors';
import type { VirtualAccountDetail } from '@/api/virtualAccounts';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { GuardedDialog } from '@/components/GuardedDialog';
import { MoneyText } from '@/components/MoneyText';
import { SubmitButton } from '@/components/SubmitButton';
import { formatMoneyMinor, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { categoricalFor } from '@/theme/categorical';
import { resolveTokens } from '@/theme/theme';
import { generateRequestId } from '@/utils/requestId';
import { availableOf } from './assignSides';

/** The bulk route's cap (TMMAPI-0145). */
export const BULK_ASSIGN_MAX = 50;

interface SplitUnassignedDialogProps {
  virtualAccount: VirtualAccountDetail;
  envelopes: Envelope[];
  onClose: () => void;
  onSplit: () => void | Promise<void>;
  onSubscriptionRequired: () => void;
}

export function SplitUnassignedDialog({
  virtualAccount,
  envelopes,
  onClose,
  onSplit,
  onSubscriptionRequired,
}: SplitUnassignedDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const fullScreen = useDialogFullScreen();
  const titleId = useId();
  const subtitleId = useId();
  const currency = virtualAccount.currency;
  const [amounts, setAmounts] = useState<Record<number, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = useRef<string | null>(null);
  const parsed = envelopes.map((envelope) => {
    const text = (amounts[envelope.id] ?? '').trim();
    return {
      envelope,
      text,
      minor: text.length === 0 ? 0 : parseMoneyToMinor(text, currency, locale),
    };
  });
  const lines = parsed.filter((row) => row.minor !== null && row.minor > 0);
  const invalid = parsed.some((row) => row.minor === null || (row.minor !== null && row.minor < 0));
  const placed = lines.reduce((sum, row) => sum + (row.minor ?? 0), 0);
  const unassigned = virtualAccount.unassignedMinor;
  const remaining = unassigned === null ? null : unassigned - placed;
  const tooMany = lines.length > BULK_ASSIGN_MAX;
  const valid = lines.length > 0 && !invalid && !tooMany;
  const guard = useDialogDirtyGuard(
    Object.values(amounts).some((text) => text.trim().length > 0),
    onClose,
    submitting,
  );

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    if (key.current === null) key.current = generateRequestId();
    try {
      await allocationsApi.assignBulk(
        virtualAccount.id,
        lines.map((row) => ({
          from: { unassigned: true },
          to: { envelopeId: row.envelope.id },
          amountMinor: row.minor ?? 0,
        })),
        key.current,
      );
      await onSplit();
      onClose();
    } catch (caught) {
      key.current = null;
      const code = isApiError(caught) ? caught.code : ErrorCodes.UNKNOWN;
      if (code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        onClose();
        onSubscriptionRequired();
        return;
      }
      const available = availableOf(caught);
      setError(
        code === 'INSUFFICIENT_TO_ASSIGN' && available !== null
          ? t('virtualAccount.moveMoney.insufficient', {
              amount: formatMoneyMinor(available, currency, locale),
            })
          : code === 'INSUFFICIENT_TO_ASSIGN'
            ? t('allocations.errorInsufficientToAssign')
            : t('virtualAccount.errorGeneric'),
      );
      guard.focusErrors();
      setSubmitting(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId={titleId}
      subtitle={
        <DialogSubtitle id={subtitleId}>{t('virtualAccount.splitUnassigned.sub')}</DialogSubtitle>
      }
      subtitleId={subtitleId}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      sx={{ '& .MuiDialog-paper': { maxWidth: fullScreen ? undefined : 520 } }}
    >
      <Box
        component="form"
        onSubmit={(event: FormEvent<HTMLFormElement>) => void handleSubmit(event)}
        noValidate
      >
        <DialogTitle id={titleId}>{t('virtualAccount.splitUnassigned.title')}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {error !== null && <Alert severity="error">{error}</Alert>}
            {tooMany && (
              <Alert severity="warning">
                {t('virtualAccount.splitUnassigned.tooMany', { count: BULK_ASSIGN_MAX })}
              </Alert>
            )}
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}
            >
              <Typography sx={{ fontSize: 13, color: tokens.text2 }}>
                {t('virtualAccount.unassigned')}
              </Typography>
              <MoneyText minor={unassigned} currency={currency} tone="signed" variant="body2" />
            </Stack>
            <Box sx={{ borderTop: `1px solid ${tokens.line}` }}>
              {envelopes.length === 0 && (
                <Typography sx={{ py: 1.25, fontSize: 13, color: tokens.text2 }}>
                  {t('virtualAccount.envelopes.empty')}
                </Typography>
              )}
              {parsed.map((row) => (
                <Stack
                  key={row.envelope.id}
                  direction="row"
                  data-split-unassigned-row={row.envelope.id}
                  sx={{
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1,
                    borderBottom: `1px solid ${tokens.line}`,
                  }}
                >
                  <Box
                    component="span"
                    aria-hidden
                    sx={{
                      flex: '0 0 8px',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: categoricalFor(row.envelope.id, tokens).line,
                    }}
                  />
                  <Typography sx={{ flex: 1, minWidth: 0, fontSize: 13 }} noWrap>
                    {row.envelope.name}
                  </Typography>
                  <TextField
                    size="small"
                    value={amounts[row.envelope.id] ?? ''}
                    onChange={(event) =>
                      setAmounts((prev) => ({ ...prev, [row.envelope.id]: event.target.value }))
                    }
                    error={row.minor === null || (row.minor !== null && row.minor < 0)}
                    slotProps={{
                      htmlInput: {
                        inputMode: 'decimal',
                        'aria-label': t('virtualAccount.split.amountFor', {
                          destination: row.envelope.name,
                        }),
                        style: { textAlign: 'end' },
                      },
                    }}
                    sx={{ width: 140 }}
                  />
                </Stack>
              ))}
            </Box>
            <Stack
              direction="row"
              role="status"
              aria-live="polite"
              sx={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}
            >
              <Typography sx={{ fontSize: 13, color: tokens.text2 }}>
                {t('virtualAccount.splitUnassigned.remaining')}
              </Typography>
              <MoneyText minor={remaining} currency={currency} tone="signed" variant="body2" />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.requestClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" disabled={!valid} loading={submitting}>
            {t('virtualAccount.splitUnassigned.action')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </GuardedDialog>
  );
}
